import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ManageAccounts as ManageAccountsIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';

const UsuariosList = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosComRoles, setUsuariosComRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtroAtivo, setFiltroAtivo] = useState(null);
  const [busca, setBusca] = useState('');
  const [dialogConfirmacao, setDialogConfirmacao] = useState({ open: false, tipo: '', usuario: null });

  useEffect(() => {
    carregarUsuarios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, filtroAtivo]);

  const carregarUsuarios = async () => {
    setLoading(true);
    setError('');
    try {
      const dados = await adminService.listarUsuarios(pagina, 50, filtroAtivo);
      const usuariosList = dados.usuarios || [];
      setUsuarios(usuariosList);
      setTotalPaginas(dados.paginacao?.total_paginas || 1);
      setTotal(dados.paginacao?.total || 0);

      // Carregar roles de cada usuário em paralelo (opcional - não bloquear se falhar)
      const rolesPromises = usuariosList.map(async (usuario) => {
        try {
          const rolesData = await adminService.listarRolesUsuario(usuario.id);
          return { usuarioId: usuario.id, roles: rolesData.roles || [] };
        } catch (err) {
          // Não logar erro se for 404 ou problema de permissão (pode ser esperado)
          if (!err.message.includes('não encontrado') && !err.message.includes('permissão')) {
            console.warn(`Erro ao carregar roles do usuário ${usuario.id}:`, err.message);
          }
          return { usuarioId: usuario.id, roles: [] };
        }
      });

      const rolesResults = await Promise.all(rolesPromises);
      const rolesMap = {};
      rolesResults.forEach(({ usuarioId, roles }) => {
        rolesMap[usuarioId] = roles;
      });
      setUsuariosComRoles(rolesMap);
    } catch (err) {
      setError(err.message || 'Erro ao carregar usuários');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBloquear = (usuario) => {
    setDialogConfirmacao({
      open: true,
      tipo: 'bloquear',
      usuario
    });
  };

  const handleDesbloquear = (usuario) => {
    setDialogConfirmacao({
      open: true,
      tipo: 'desbloquear',
      usuario
    });
  };

  const handleExcluir = (usuario) => {
    setDialogConfirmacao({
      open: true,
      tipo: 'excluir',
      usuario
    });
  };

  const confirmarAcao = async () => {
    const { tipo, usuario } = dialogConfirmacao;
    
    try {
      if (tipo === 'bloquear') {
        await adminService.bloquearUsuario(usuario.id);
      } else if (tipo === 'desbloquear') {
        await adminService.desbloquearUsuario(usuario.id);
      } else if (tipo === 'excluir') {
        await adminService.excluirUsuario(usuario.id);
      }
      
      setDialogConfirmacao({ open: false, tipo: '', usuario: null });
      carregarUsuarios();
    } catch (err) {
      setError(err.message || 'Erro ao executar ação');
    }
  };

  const usuariosFiltrados = usuarios.filter(usuario => {
    if (!busca) return true;
    const buscaLower = busca.toLowerCase();
    return (
      usuario.nome?.toLowerCase().includes(buscaLower) ||
      usuario.cpf?.includes(busca) ||
      usuario.nome_de_usuario?.toLowerCase().includes(buscaLower)
    );
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Gestão de Usuários
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/usuarios/criar')}
        >
          Criar Usuário
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por nome, CPF ou usuário..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <ToggleButtonGroup
            value={filtroAtivo}
            exclusive
            onChange={(e, newValue) => setFiltroAtivo(newValue)}
            size="small"
          >
            <ToggleButton value={null}>Todos</ToggleButton>
            <ToggleButton value={true}>Ativos</ToggleButton>
            <ToggleButton value={false}>Bloqueados</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CPF</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nome de Usuário</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Roles</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuariosFiltrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum usuário encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    usuariosFiltrados.map((usuario) => {
                      const rolesDoUsuario = usuariosComRoles[usuario.id] || [];
                      return (
                        <TableRow key={usuario.id} hover>
                          <TableCell>{usuario.id}</TableCell>
                          <TableCell>{usuario.nome}</TableCell>
                          <TableCell>{usuario.cpf}</TableCell>
                          <TableCell>{usuario.nome_de_usuario}</TableCell>
                          <TableCell>
                            <Chip
                              label={usuario.ativo ? 'Ativo' : 'Bloqueado'}
                              color={usuario.ativo ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {rolesDoUsuario.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {rolesDoUsuario.map((role) => (
                                  <Chip
                                    key={role.id || role.identificador}
                                    label={role.nome || role.role?.nome}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Nenhuma role
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/admin/usuarios/editar/${usuario.id}`)}
                              title="Editar"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/admin/usuarios/roles/${usuario.id}`)}
                              title="Gerenciar Roles"
                            >
                              <ManageAccountsIcon fontSize="small" />
                            </IconButton>
                            {usuario.ativo ? (
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleBloquear(usuario)}
                                title="Bloquear"
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleDesbloquear(usuario)}
                                title="Desbloquear"
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleExcluir(usuario)}
                              title="Excluir"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPaginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPaginas}
                  page={pagina}
                  onChange={(e, value) => setPagina(value)}
                  color="primary"
                />
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Total: {total} usuário(s)
            </Typography>
          </>
        )}
      </Paper>

      {/* Dialog de Confirmação */}
      <Dialog open={dialogConfirmacao.open} onClose={() => setDialogConfirmacao({ open: false, tipo: '', usuario: null })}>
        <DialogTitle>
          {dialogConfirmacao.tipo === 'bloquear' && 'Bloquear Usuário'}
          {dialogConfirmacao.tipo === 'desbloquear' && 'Desbloquear Usuário'}
          {dialogConfirmacao.tipo === 'excluir' && 'Excluir Usuário'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogConfirmacao.tipo === 'bloquear' && `Deseja realmente bloquear o usuário "${dialogConfirmacao.usuario?.nome}"?`}
            {dialogConfirmacao.tipo === 'desbloquear' && `Deseja realmente desbloquear o usuário "${dialogConfirmacao.usuario?.nome}"?`}
            {dialogConfirmacao.tipo === 'excluir' && `Deseja realmente excluir o usuário "${dialogConfirmacao.usuario?.nome}"? Esta ação não pode ser desfeita.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogConfirmacao({ open: false, tipo: '', usuario: null })}>
            Cancelar
          </Button>
          <Button onClick={confirmarAcao} color={dialogConfirmacao.tipo === 'excluir' ? 'error' : 'primary'} variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsuariosList;

